import { z } from 'zod';

/**
 * Impostazioni provider di autenticazione tenant (LDAP/AD + OIDC):
 * `/api/:slug/auth-provider/*`. Source of truth:
 * `packages/backend/src/routes/security/auth-provider.routes.ts`.
 *
 *  - `GET  /auth-provider/config`              → `data: AuthProviderConfigData`
 *  - `PUT  /auth-provider/config` (ldap)       → `data: { ldap }`
 *  - `PUT  /auth-provider/config` (oidc)       → `data: { oidc }`
 *  - `POST /auth-provider/test-connection`     → `{ success: <esito test>, data: LdapTestResult }` (HTTP 200 anche su esito negativo)
 *  - `POST /auth-provider/test-oidc-discovery` → `{ success: <esito test>, data: OidcTestResult }`
 *  - `POST /auth-provider/sync-users`          → `data: LdapSyncResult`
 *  - `POST /auth-provider/create-unmatched`    → `data: LdapCreateUnmatchedResult`
 *  - `POST /auth-provider/merge-user`          → `{ success, message }`
 */

export const ldapProviderConfigSchema = z
  .object({
    host: z.string(),
    port: z.number(),
    security: z.string(),
    timeout: z.number(),
    bindUser: z.string(),
    bindDn: z.string(),
    /** Mascherata (`SECRET_MASK`) in lettura quando presente. */
    bindPassword: z.string(),
    baseDn: z.string(),
    userBaseDn: z.string(),
    userFilter: z.string(),
    searchScope: z.string(),
    attributeMapping: z.record(z.string()),
    groupBaseDn: z.string(),
    groupFilter: z.string(),
    groupMembershipAttribute: z.string(),
    nestedGroups: z.boolean(),
    tlsVerify: z.boolean(),
    tlsCaCert: z.string(),
    enabled: z.boolean(),
  })
  .passthrough();
export type LdapProviderConfig = z.infer<typeof ldapProviderConfigSchema>;

/** Config OIDC come salvata/mascherata dal backend: ogni campo può mancare. */
export const oidcProviderConfigSchema = z
  .object({
    enabled: z.boolean().optional(),
    issuer: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    scopes: z.string().optional(),
    authorizationEndpoint: z.string().optional(),
    tokenEndpoint: z.string().optional(),
    jwksUri: z.string().optional(),
    claimMapping: z
      .object({
        email: z.string().optional(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
      })
      .optional(),
    trustIdpMfa: z.boolean().optional(),
  })
  .passthrough();
export type OidcProviderConfig = z.infer<typeof oidcProviderConfigSchema>;

export const authProviderConfigDataSchema = z
  .object({
    ldap: ldapProviderConfigSchema,
    oidc: oidcProviderConfigSchema,
    activeProvider: z.enum(['ldap', 'local']),
    ssoEnabled: z.boolean(),
  })
  .passthrough();
export type AuthProviderConfigData = z.infer<typeof authProviderConfigDataSchema>;

export const authProviderLdapSavedDataSchema = z.object({ ldap: ldapProviderConfigSchema });
export type AuthProviderLdapSavedData = z.infer<typeof authProviderLdapSavedDataSchema>;

export const authProviderOidcSavedDataSchema = z.object({ oidc: oidcProviderConfigSchema });
export type AuthProviderOidcSavedData = z.infer<typeof authProviderOidcSavedDataSchema>;

export const ldapTestResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    userCount: z.number().optional(),
    details: z
      .object({
        hostReachable: z.boolean().optional(),
        bindSuccessful: z.boolean().optional(),
        searchSuccessful: z.boolean().optional(),
      })
      .optional(),
  })
  .passthrough();
export type LdapTestResult = z.infer<typeof ldapTestResultSchema>;

export const oidcTestResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    details: z
      .object({
        discoverySuccessful: z.boolean().optional(),
        jwksReachable: z.boolean().optional(),
        issuer: z.string().optional(),
      })
      .optional(),
  })
  .passthrough();
export type OidcTestResult = z.infer<typeof oidcTestResultSchema>;

export const ldapSyncResultSchema = z.object({
  autoLinked: z.array(z.object({ localUserId: z.string(), email: z.string(), adObjectGuid: z.string() })),
  unmatched: z.array(
    z.object({
      adObjectGuid: z.string(),
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      displayName: z.string(),
      samAccountName: z.string(),
    }),
  ),
  created: z.array(z.object({ localUserId: z.string(), email: z.string() })),
  errors: z.array(z.object({ email: z.string(), error: z.string() })),
  summary: z.object({
    totalAd: z.number(),
    autoLinked: z.number(),
    unmatched: z.number(),
    created: z.number(),
    errors: z.number(),
  }),
});
export type LdapSyncResult = z.infer<typeof ldapSyncResultSchema>;

export const ldapCreateUnmatchedResultSchema = z.object({
  created: z.array(z.object({ localUserId: z.string(), email: z.string() })),
  errors: z.array(z.object({ email: z.string(), error: z.string() })),
});
export type LdapCreateUnmatchedResult = z.infer<typeof ldapCreateUnmatchedResultSchema>;
